/**
 * Release notes are authored as free text, so a card that prints them verbatim
 * shows one grey block where a section heading, a feature name and a sentence
 * all carry the same weight. Splitting the text into sections and items lets
 * the card give each part its own typography and stay scannable at a glance.
 */
export interface ReleaseNoteItem {
  /** Lead-in before the first colon, "Persona opportunities". Null when the line has none. */
  label: string | null;
  text: string;
}

export interface ReleaseNoteSection {
  /** "New", "Fixed" — null for notes that never declare one. */
  heading: string | null;
  items: ReleaseNoteItem[];
}

const BULLET_MARKER = /^[•\-*–—]\s*/;

/** Longer lead-ins are prose containing a colon, not a feature name. */
const MAX_LABEL_LENGTH = 40;

export function parseReleaseNotes(notes: string | null): ReleaseNoteSection[] {
  const lines = (notes ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const sections: ReleaseNoteSection[] = [];

  lines.forEach((line, index) => {
    if (isHeading(line, lines[index + 1])) {
      sections.push({ heading: line, items: [] });
      return;
    }

    const current = sections.at(-1) ?? pushSection(sections);
    current.items.push(toItem(line.replace(BULLET_MARKER, "")));
  });

  return sections.filter((section) => section.items.length > 0);
}

export function countReleaseNoteItems(sections: ReleaseNoteSection[]): number {
  return sections.reduce((total, section) => total + section.items.length, 0);
}

/**
 * Keeps the first `limit` items in document order and drops the sections left
 * empty, so a collapsed card never shows a heading with nothing under it.
 */
export function limitReleaseNoteItems(
  sections: ReleaseNoteSection[],
  limit: number,
): ReleaseNoteSection[] {
  let remaining = limit;

  return sections
    .map((section) => {
      const items = section.items.slice(0, Math.max(remaining, 0));
      remaining -= items.length;
      return { ...section, items };
    })
    .filter((section) => section.items.length > 0);
}

/** A bare line is a heading only when it introduces bullets; otherwise it is a note of its own. */
function isHeading(line: string, next: string | undefined): boolean {
  return !BULLET_MARKER.test(line) && next !== undefined && BULLET_MARKER.test(next);
}

function toItem(text: string): ReleaseNoteItem {
  const match = /^([^:]+):\s+(.+)$/.exec(text);
  if (!match || match[1].length > MAX_LABEL_LENGTH) return { label: null, text };

  return { label: match[1], text: match[2] };
}

function pushSection(sections: ReleaseNoteSection[]): ReleaseNoteSection {
  const section: ReleaseNoteSection = { heading: null, items: [] };
  sections.push(section);
  return section;
}
